import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BaseQueryParams } from '@shared';
import { buildQueryParams } from '@shared/utils';
import { map, Observable } from 'rxjs';
import { ApiGetAllResponse } from '../interfaces';
import { SharedService } from '../shared/shared.service';
import { ApiHostLocation, ApiHostStats } from './hosts.interfaces';

/**
 * Service for managing host locations and their associated metadata.
 *
 * @description
 * Provides methods for fetching paginated host data with integrated caching
 * using the SharedService's centralized cache management.
 */
@Injectable({
    providedIn: 'root',
})
export class HostsService {
    /**
     * Angular HTTP client for API communication.
     * @type {HttpClient}
     */
    private readonly _http = inject(HttpClient);
    /**
     * Shared service for common utilities.
     */
    private readonly _sharedService = inject(SharedService);

    /**
     * Base URL for the hosts API endpoints.
     * Routed through the BFF proxy in `server.ts`.
     */
    private readonly _baseUrl = '/api/hosts';

    /**
     * Internal cache for host queries.
     */
    private readonly _cache = new Map<string, Observable<ApiGetAllResponse<ApiHostLocation>>>();

    /**
     * Retrieves a paginated list of hosts.
     *
     * @param params - Optional query parameters for filtering
     * @returns Observable of paginated host response
     */
    public getAll(params?: BaseQueryParams): Observable<ApiGetAllResponse<ApiHostLocation>> {
        const mergedParams: BaseQueryParams = { ...params, limit: 15 };
        const queryParams = buildQueryParams(mergedParams);
        const cacheKey = `getAll-${JSON.stringify(mergedParams)}`;

        return this._sharedService.getCached(this._cache, cacheKey, () =>
            this._http.get<ApiGetAllResponse<ApiHostLocation>>(this._baseUrl, {
                params: queryParams,
            }),
        );
    }

    /**
     * Retrieves a single host by its ID.
     *
     * @param id - The UUID of the host location
     * @returns Observable of the host location details
     */
    public getHostById(id: string): Observable<ApiHostLocation> {
        return this._http
            .get<{ data: ApiHostLocation }>(`${this._baseUrl}/${id}`)
            .pipe(map((res) => res.data));
    }

    /**
     * Retrieves paginated hosts owned by a dealer.
     *
     * @param userId - The user id of the dealer
     */
    public getHostsByDealer(userId: string): Observable<ApiGetAllResponse<ApiHostLocation>> {
        const queryParams = buildQueryParams({
            filters: {
                ownerId: { _eq: userId },
            },
            limit: 25,
        });
        return this._http.get<ApiGetAllResponse<ApiHostLocation>>(this._baseUrl, {
            params: queryParams,
        });
    }

    /**
     * Retrieves host statistics (total, active, inactive) grouped by weekly buckets.
     *
     * @returns Observable of host statistics
     */
    public getStats(): Observable<ApiHostStats> {
        return this._http.get<ApiHostStats>('/api/hosts/stats');
    }

    /**
     * Clears the host locations cache.
     */
    public clearCache(): void {
        this._cache.clear();
    }
}
